/**
 * @module features/client-device-management/components/OrgAssignedDevicesList
 * @description Adaptive assigned-devices data grid and mobile stacked card list for
 * `{Org} Device Management`. Surfaces operational warning indicators when an active
 * device has zero WiFi profiles configured, and provides quick triggers for dynamic
 * configuration editing, WiFi profile binding, and prompt-driven unassignment.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  MapPin,
  Pencil,
  SlidersHorizontal,
  TriangleAlert,
  Unlink,
  Wifi,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AssignmentSummary, formatDeviceTypeLabel } from "../types";

export interface OrgAssignedDevicesListProps {
  assignments: AssignmentSummary[];
  deviceTypes?: ReadonlyArray<{ type_id: string; label: string }>;
  onEdit: (assignment: AssignmentSummary) => void;
  onConfigure: (assignment: AssignmentSummary) => void;
  onManageWifi: (assignment: AssignmentSummary) => void;
  onUnassign: (assignment: AssignmentSummary) => void;
}

function getStatusBadgeClasses(status: AssignmentSummary["status"]): {
  pill: string;
  dot: string;
  label: string;
} {
  if (status === "active") {
    return {
      pill: "bg-success-soft text-success border-success/20",
      dot: "bg-success",
      label: "Active",
    };
  }
  if (status === "archived") {
    return {
      pill: "bg-warning-soft text-warning border-warning/20",
      dot: "bg-warning",
      label: "Archived",
    };
  }
  return {
    pill: "bg-muted text-muted-foreground border-border",
    dot: "bg-muted-foreground",
    label: "Unassigned",
  };
}

export function OrgAssignedDevicesList({
  assignments,
  deviceTypes,
  onEdit,
  onConfigure,
  onManageWifi,
  onUnassign,
}: OrgAssignedDevicesListProps) {
  const navigate = useNavigate();
  const captionId = React.useId();

  return (
    <div className="@container w-full">
      {/* Desktop & Tablet Semantic Sticky Table (container >=640px) */}
      <div
        tabIndex={0}
        role="region"
        aria-labelledby={captionId}
        className="hidden @[640px]:block rounded-2xl border border-border/80 bg-card shadow-card overflow-auto max-h-[min(640px,72dvh)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <table className="w-full border-separate border-spacing-0 text-sm tabular-nums">
          <caption id={captionId} className="sr-only">
            Assigned hardware devices for this organization
          </caption>
          <thead className="sticky top-0 z-sticky bg-card/95 backdrop-blur-sm">
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-sticky bg-card/95 h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Device
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Gateway Type
              </th>
              <th
                scope="col"
                className="hidden @[768px]:table-cell h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Location
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                WiFi Profile
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Status
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-right align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((assignment) => {
              const statusStyle = getStatusBadgeClasses(assignment.status);
              const isActive = assignment.status === "active";
              const displayName =
                assignment.custom_device_name || assignment.device_name;
              const typeLabel = formatDeviceTypeLabel(
                assignment.device_type,
                deviceTypes
              );

              return (
                <tr
                  key={assignment.id}
                  className="group transition-colors hover:bg-muted/40"
                >
                  {/* Device Name + Hardware Identifiers */}
                  <th
                    scope="row"
                    className="sticky left-0 bg-card group-hover:bg-muted/40 transition-colors px-4 py-3 text-left align-middle font-normal border-b border-border/60"
                  >
                    <div className="min-w-0 space-y-0.5">
                      <p className="font-medium text-sm text-foreground truncate max-w-[240px]">
                        {displayName}
                      </p>
                      {assignment.custom_device_name &&
                        assignment.custom_device_name !== assignment.device_name && (
                          <p className="text-[11px] text-muted-foreground truncate max-w-[240px]">
                            Hardware: {assignment.device_name}
                          </p>
                        )}
                      <p className="font-mono text-[11px] leading-4 text-muted-foreground truncate">
                        {assignment.registered_device_id}
                        {assignment.mac_address
                          ? ` · ${assignment.mac_address}`
                          : ""}
                      </p>
                      {assignment.notes && (
                        <p
                          className="text-[11px] text-muted-foreground truncate max-w-[240px]"
                          title={assignment.notes}
                        >
                          Note: {assignment.notes}
                        </p>
                      )}
                    </div>
                  </th>

                  {/* Device Type */}
                  <td className="px-4 py-3 align-middle border-b border-border/60">
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                      {typeLabel}
                    </span>
                  </td>

                  {/* Deployment Location */}
                  <td className="hidden @[768px]:table-cell px-4 py-3 align-middle text-[13px] leading-[18px] border-b border-border/60">
                    {assignment.location ? (
                      <div className="flex items-center gap-1.5 text-foreground">
                        <MapPin
                          className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                          aria-hidden="true"
                        />
                        <span className="truncate max-w-[180px]">
                          {assignment.location}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not set</span>
                    )}
                  </td>

                  {/* Active WiFi Profile or Operational Warning Badge (FR-023) */}
                  <td className="px-4 py-3 align-middle border-b border-border/60">
                    {assignment.active_wifi_ssid ? (
                      <button
                        type="button"
                        onClick={() => onManageWifi(assignment)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-success-soft text-success border border-success/20 px-2.5 py-1 text-[11px] font-medium hover:opacity-90 transition-opacity touch:min-h-[44px]"
                        title="Inspect or switch active WiFi profile"
                      >
                        <Wifi className="h-3 w-3 shrink-0" aria-hidden="true" />
                        <span className="truncate max-w-[130px] font-mono">
                          {assignment.active_wifi_ssid}
                        </span>
                      </button>
                    ) : isActive ? (
                      <button
                        type="button"
                        onClick={() => onManageWifi(assignment)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-warning-soft text-warning border border-warning/20 px-2.5 py-1 text-[11px] font-medium hover:opacity-90 transition-opacity touch:min-h-[44px]"
                        title="No active WiFi profile configured — click to bind one"
                      >
                        <TriangleAlert
                          className="h-3 w-3 shrink-0"
                          aria-hidden="true"
                        />
                        <span>No active WiFi</span>
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Assignment Status */}
                  <td className="px-4 py-3 align-middle border-b border-border/60">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${statusStyle.pill}`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`}
                        aria-hidden="true"
                      />
                      {statusStyle.label}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 align-middle text-right border-b border-border/60">
                    <div className="inline-flex items-center justify-end gap-1.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/s-admin/devices/${assignment.device_id}/telemetry`)}
                        className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1"
                        aria-label={`View telemetry for ${displayName}`}
                        title="View live telemetry, gauges and logs"
                      >
                        <Activity className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
                        <span>Telemetry</span>
                      </Button>
                      {isActive && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(assignment)}
                          className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1"
                          aria-label={`Edit details for ${displayName}`}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>Edit</span>
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onConfigure(assignment)}
                        className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1"
                        aria-label={`Configure ${displayName}`}
                      >
                        <SlidersHorizontal
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        <span>Config</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onManageWifi(assignment)}
                        className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1"
                        aria-label={`Manage WiFi for ${displayName}`}
                      >
                        <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>WiFi</span>
                      </Button>
                      {isActive && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onUnassign(assignment)}
                          className="h-8 touch:min-h-[44px] px-2.5 text-xs text-danger hover:text-danger hover:bg-danger-soft gap-1"
                          aria-label={`Unassign ${displayName}`}
                        >
                          <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                          <span className="hidden xl:inline">Unassign</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Cards (container <640px) */}
      <div className="grid grid-cols-1 gap-3 @[640px]:hidden" role="list">
        {assignments.map((assignment) => {
          const statusStyle = getStatusBadgeClasses(assignment.status);
          const isActive = assignment.status === "active";
          const displayName =
            assignment.custom_device_name || assignment.device_name;
          const typeLabel = formatDeviceTypeLabel(
            assignment.device_type,
            deviceTypes
          );

          return (
            <div
              key={assignment.id}
              role="listitem"
              className="rounded-2xl border border-border/80 bg-card p-4 shadow-card space-y-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-medium text-sm text-foreground truncate">
                    {displayName}
                  </h3>
                  <p className="font-mono text-[11px] leading-4 text-muted-foreground truncate mt-0.5">
                    {assignment.registered_device_id}
                    {assignment.mac_address ? ` · ${assignment.mac_address}` : ""}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${statusStyle.pill}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`}
                    aria-hidden="true"
                  />
                  {statusStyle.label}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                  {typeLabel}
                </span>

                {assignment.active_wifi_ssid ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-soft text-success border border-success/20 px-2.5 py-0.5 text-[11px] font-medium font-mono">
                    <Wifi className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {assignment.active_wifi_ssid}
                  </span>
                ) : isActive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft text-warning border border-warning/20 px-2.5 py-0.5 text-[11px] font-medium">
                    <TriangleAlert
                      className="h-3 w-3 shrink-0"
                      aria-hidden="true"
                    />
                    No active WiFi
                  </span>
                ) : null}
              </div>

              {assignment.location && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{assignment.location}</span>
                </div>
              )}

              {assignment.notes && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  Note: {assignment.notes}
                </p>
              )}

              {/* Touch-friendly 44px minimum height actions */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/s-admin/devices/${assignment.device_id}/telemetry`)}
                  className="min-h-[44px] text-xs gap-1"
                  aria-label={`View telemetry for ${displayName}`}
                >
                  <Activity className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
                  <span>Telemetry</span>
                </Button>
                {isActive && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(assignment)}
                    className="min-h-[44px] text-xs gap-1"
                    aria-label={`Edit details for ${displayName}`}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Edit</span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigure(assignment)}
                  className="min-h-[44px] text-xs gap-1"
                >
                  <SlidersHorizontal
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  <span>Config</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onManageWifi(assignment)}
                  className="min-h-[44px] text-xs gap-1"
                >
                  <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>WiFi</span>
                </Button>
                {isActive && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onUnassign(assignment)}
                    className="min-h-[44px] text-xs text-danger border-danger/20 hover:bg-danger-soft gap-1"
                  >
                    <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Unassign</span>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Geometry-matched skeleton for OrgAssignedDevicesList (CLS = 0.00).
 */
export function OrgAssignedDevicesSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div
      className="@container w-full"
      aria-busy="true"
      aria-label="Loading assigned devices"
    >
      <div className="hidden @[640px]:block rounded-2xl border border-border/80 bg-card shadow-card overflow-hidden">
        <div className="h-11 px-4 border-b border-border/80 flex items-center justify-between bg-muted/20">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-16" />
          <Skeleton className="h-3.5 w-28" />
        </div>
        <div className="divide-y divide-border/60">
          {Array.from({ length: rows }).map((_, idx) => (
            <div
              key={idx}
              className="px-4 py-3 flex items-center justify-between gap-4"
            >
              <div className="space-y-1.5 flex-1 max-w-[200px]">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-44" />
              </div>
              <Skeleton className="h-5 w-28 rounded-full" />
              <Skeleton className="hidden @[768px]:block h-4 w-28" />
              <Skeleton className="h-6 w-28 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <div className="flex items-center gap-1.5">
                <Skeleton className="h-8 w-16 rounded-md" />
                <Skeleton className="h-8 w-16 rounded-md" />
                <Skeleton className="h-8 w-20 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 @[640px]:hidden">
        {Array.from({ length: 3 }).map((_, idx) => (
          <div
            key={idx}
            className="rounded-2xl border border-border/80 bg-card p-4 shadow-card space-y-3"
          >
            <div className="flex items-start justify-between">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-44" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-5 w-28 rounded-full" />
              <Skeleton className="h-5 w-28 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/60">
              <Skeleton className="h-11 w-full rounded-md" />
              <Skeleton className="h-11 w-full rounded-md" />
              <Skeleton className="h-11 w-full rounded-md" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
